import { Router, useMatch } from '@reach/router';
import { Toc, TocProvider } from '../components/TableOfContents';

import ButtonSection from '../components/Workspace/ButtonSection';
import Chapter from '../components/Workspace/Chapter';
import Features from '../components/Workspace/Features';
import GetInTouch from '../components/Workspace/GetInTouch';
import Head from '../components/Head';
import HotspotHero from '../components/Workspace/HotspotHero';
import PageHeading from '../components/Workspace/PageHeading';
import React from 'react';
import RoomDownloads from '../components/Workspace/RoomDownloads';
import RoomExperience from '../components/Workspace/RoomExperience';
import WorkspaceB from './workspaceb';
import WorkspaceProducts from '../components/Workspace/WorkspaceProducts';
import WorkspaceToggle from '../components/Workspace/WorkspaceToggle';
import { edgeToEdgeImageSizes } from '../utils';
import { graphql } from 'gatsby';
import styles from './workspace.module.scss';

// Workspace id's:
const AltWorkspaces = {
  executiveRoom: 'b4900e37-6a2d-5dfb-b06a-2f62a185b194',
  businessClass: 'ee9449a2-374f-52da-a3b7-a6766ff6a278',
  // conference: '7d7d398b-b4df-54c9-8ea1-532e9e6d5269', // dual spaces not yet supported in B mode!
};

export const query = graphql`
  query($id: String!) {
    contentfulWorkspaceSubCategory(id: { eq: $id }) {
      ...WorkspaceSubCategory
    }
  }
`;

export default function WorkspaceAB(props) {
  // if (props.data.contentfulWorkspaceSubCategory.workspaces[0].id === 'ee9449a2-374f-52da-a3b7-a6766ff6a278') {
  let id = props.data.contentfulWorkspaceSubCategory.workspaces[0].id;
  if (Object.values(AltWorkspaces).indexOf(id) !== -1) {
    let [workspace] = props.data.contentfulWorkspaceSubCategory.workspaces;
    let data = { data: { workspace, description: props.data.contentfulWorkspaceSubCategory.description } };
    return <WorkspaceB {...data} />;
  }
  return <Workspace {...props} />;
}

function Workspace({ data, pageContext, path, ...props }) {
  const matchedPath = useMatch(pageContext.matchPath) || {};
  const pageMatch = matchedPath['*'] || pageContext.slug;
  const {
    bundles,
    featuresHeading,
    features,
    hotspotHero,
    description,
    heading,
    mainProductTitle,
    roomExperience,
    pageVariantsMeta,
    infoboxAltPosition,
  } = assertData(data, pageMatch);

  const basepath = path.replace(/\/\*/, '');
  // const { categoryTitle } = pageContext;
  const defaultImage = edgeToEdgeImageSizes(hotspotHero?.image);

  const variantLinks = pageVariantsMeta.map(({ heading, shortHeading, slug }) => {
    const link = `${basepath}/${slug}`;
    return { title: shortHeading ? shortHeading : heading, slug, link };
  });
  const [defaultWorkspaceLink] = variantLinks;

  return (
    <TocProvider>
      <Head location={props.location} title={heading} description={description} image={hotspotHero?.image?.file.url} />

      <Toc heading={heading} workspaceLink={defaultWorkspaceLink.link} />
      {Boolean(bundles) && (
        <ButtonSection
          placeholderImage={defaultImage}
          heightMultiplier={2 + (features?.length || 2)} // ButtonSection scroll distance
          altPosition={infoboxAltPosition}
          bundles={bundles}
        >
          <Router basepath={basepath} primary={false}>
            <WorkspaceToggle pages={variantLinks} path="/" page={pageContext.slug} />
            <WorkspaceToggle pages={variantLinks} path={`/:page`} />
          </Router>
        </ButtonSection>
      )}

      {/* PageHeading section */}
      <PageHeading heading={heading} description={description} image={defaultImage} />

      {/* HotspotHero section */}
      {Boolean(hotspotHero) && (
        <Chapter title="The room">
          <HotspotHero hotspotHero={hotspotHero} infocardAlternatePos={infoboxAltPosition} />
        </Chapter>
      )}

      {/* Features section / Workspace' Ways to collaborate section  */}
      {Boolean(features) && (
        <Chapter title="Ways to collaborate">
          <Features
            defaultImage={hotspotHero?.image}
            featuresHeading={featuresHeading}
            features={features}
            productTitle={mainProductTitle}
            infocardAlternatePos={infoboxAltPosition}
          />
        </Chapter>
      )}

      {/* WorkspaceProducts section */}
      {Boolean(bundles) && (
        <Chapter className={styles.whiteBackground} title="Products">
          <WorkspaceProducts
            bundles={bundles}
            /* categoryTitle={categoryTitle} -- disabled until sub section is fixed */
          />
        </Chapter>
      )}

      {Boolean(roomExperience) && (
        <Chapter className={styles.whiteBackground} title="Room Experience" Tag="div">
          <RoomExperience roomExperience={roomExperience} />
        </Chapter>
      )}

      {Boolean(features) && <RoomDownloads assets={[data.contentfulWorkspaceSubCategory, ...features]} />}

      <Chapter className={styles.whiteBackground} title="Get Quote">
        <GetInTouch />
      </Chapter>
    </TocProvider>
  );
}

function assertData(data, pageSlug) {
  const { workspaces, heading, description: descriptionMeta } = data.contentfulWorkspaceSubCategory;
  const { description = '' } = descriptionMeta ? descriptionMeta : {};
  let { bundles, featuresHeading, features, featuresInfoboxAlternatePosition, hotspotHero, roomExperience } =
    data.contentfulWorkspaceSubCategory.workspaces.find(page => page.slug === pageSlug) || {};

  const pageVariantsMeta = workspaces
    ? workspaces.map(({ slug, heading, shortHeading }) => ({ slug, heading, shortHeading }))
    : [];
  const [mainBundle] = bundles && bundles.length ? bundles : [];
  const mainProductTitle = mainBundle?.product?.shortHeading || mainBundle?.product?.heading || '';

  const infoboxAltPosition = featuresInfoboxAlternatePosition ? featuresInfoboxAlternatePosition : false;

  return {
    bundles,
    featuresHeading,
    features,
    infoboxAltPosition,
    hotspotHero,
    description,
    heading,
    mainBundle,
    mainProductTitle,
    roomExperience,
    pageVariantsMeta,
  };
}
